// Where the profile lives. Hosted on CrazyGames: the SDK data module (follows the CG account across devices; it keeps
// guests in its own local store and merges on login — the SDK reloads the game on login). Elsewhere: localStorage.
// Saves are debounced with a trailing edge, and flushed on pagehide/visibilitychange — without the flush the last
// round's payout is lost when the tab closes (Wobble's lesson).

import * as cg from "../platform/cg";
import { freshProfile, migrate, type Profile } from "./profile";

const KEY = "tnt.profile";
let cur: Profile | null = null;
let timer: number | undefined;
const listeners: ((p: Profile) => void)[] = [];

function readRaw(): any {
  const s = (cg.hosted() ? cg.storeGet(KEY) : null) ?? (() => { try { return localStorage.getItem(KEY); } catch { return null; } })();
  if (!s) return null;
  try { return JSON.parse(s); } catch { return null; }
}
function writeRaw(p: Profile) {
  const s = JSON.stringify(p);
  if (cg.hosted() && cg.storeSet(KEY, s)) return;
  try { localStorage.setItem(KEY, s); } catch { /* private mode: the session still plays, progress is per-tab */ }
}

/** load once (after cg.ready) — a corrupt or missing save becomes a fresh profile, never a crash */
export function load(now = Date.now()): Profile {
  const raw = readRaw();
  cur = raw ? migrate(raw, now) : freshProfile(now);
  return cur;
}
export function get(): Profile { if (!cur) throw new Error("store.load() first"); return cur; }
export const isNew = () => !readRaw();

/** mutate through this: listeners repaint, the save is scheduled */
export function update(fn: (p: Profile) => void) {
  const p = get(); fn(p);
  for (const l of listeners) try { l(p); } catch (e) { console.warn("[store] listener", e); }
  schedule();
}
export const onChange = (f: (p: Profile) => void) => { listeners.push(f); };

function schedule() { if (timer !== undefined) clearTimeout(timer); timer = setTimeout(flush, 600) as unknown as number; }
export function flush() { if (timer !== undefined) { clearTimeout(timer); timer = undefined; } if (cur) writeRaw(cur); }
addEventListener("pagehide", flush);
document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") flush(); });
